import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { notify } from "shell/store/notifications";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useRenderParsleyPreviewMutation } from "shell/services/mcp";
import { WebView } from "shell/services/types";
import { useRegisterRef } from "../../../engine/useRegisterRef";
import { endStudioChatSession } from "../components/StudioAIPanel";

// The load balancer in front of PVL answers 413 at 1 MiB. The margin covers
// the multipart framing and the zuid field.
const PVL_MAX_PARSLEY_BYTES = 1024 * 1024 - 1024;
// A healthy render takes well under a second; {{this.autolayout()}} never
// answers at all.
const PVL_TIMEOUT_MS = 20000;
// Session-wide, so ids stay unique across Studio remounts: StudioAIPanel
// remembers which discard it last showed for the rest of the session.
let discardSeq = 0;

// A page path compared loosely: no query, hash or trailing slash.
const comparablePath = (path: string) => {
  const bare = path.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  try {
    return decodeURI(bare);
  } catch {
    return bare;
  }
};

// How long the canvas has to acknowledge a paint. A page without Studio's
// bridge never answers.
const PAINT_TIMEOUT_MS = 15000;

const loadsExternalStylesheet = (source: string) =>
  (source.match(/<link\b[^>]*>/gi) || []).some(
    (tag) =>
      /stylesheet/i.test(tag) && /\bhref\s*=\s*["']?(?:https?:)?\/\//i.test(tag)
  );

// Not page code: a bare ZUID, or plain text replacing a file with markup.
const BARE_ZUID = /^\d+-[0-9a-f]+-[0-9a-z]+$/i;
const HAS_CODE = /<[a-z!/]|\{\{|\(\*\*/i;
const isNotPageCode = (next: string, current: string) => {
  const trimmed = next.trim();
  return (
    BARE_ZUID.test(trimmed) ||
    (!!trimmed && !HAS_CODE.test(trimmed) && HAS_CODE.test(current))
  );
};

// What the preview is doing. Once painted, the live canvas shows it.
export type StudioAiPreview =
  | { status: "loading" }
  | { status: "painted" }
  | { status: "error"; message: string };

export type CodeRegionReplaced = {
  codeId?: string;
  ok?: boolean;
  count?: number;
  reason?: string;
  requestId?: number;
};

// Everything about the AI change that undo/redo swaps as a unit. Staging has
// no other exit than a reload, so history needs to put all of it back.
export type StudioAiState = {
  stagedCodeId: string | null;
  preview: StudioAiPreview | null;
  lastPaint: { codeId: string; html: string } | null;
};

type Args = {
  // Registers the page's view for the AI while true.
  active: boolean;
  webViews: WebView[];
  pageModelZUID: string;
  pageItemZUID: string;
  // The page model's full field objects, as the Code app sends them. `any`
  // because the fields store is untyped (see AppState's TODO).
  pageFields: any[];
  randomHashID?: string;
  pendingLayoutCodeIds: string[];
  stageLayoutSourceUpdate: (
    codeId: string,
    next: string,
    options?: { replacesSource?: boolean }
  ) => void;
  readStagedLayoutSource: (codeId: string) => string | null;
  // Runs before a change is staged; the live canvas is about to change.
  onBeforeStage: () => void;
  // The page the canvas was loaded with, and how to load it again.
  loadedPath: string;
  reloadCanvas: () => void;
  postCommandToBridge: (cmd: {
    action: string;
    codeId?: string;
    html?: string;
    requestId?: number;
    locked?: boolean;
  }) => void;
  // Runs when a preview stops loading — painted, or failed — so an AI turn's
  // undo step can close once the canvas shows its result.
  onPreviewSettled?: () => void;
};

// AI edits to the page's own view. The model's whole-file replacement is
// staged through the layout save funnel, rendered by WebEngine's PVL
// endpoint, and painted by the bridge into the view's region of the live
// canvas, so nothing reaches the instance until Save.
export const useStudioAiEdit = ({
  active,
  webViews,
  pageModelZUID,
  pageItemZUID,
  pageFields,
  randomHashID,
  pendingLayoutCodeIds,
  stageLayoutSourceUpdate,
  readStagedLayoutSource,
  onBeforeStage,
  postCommandToBridge,
  loadedPath,
  reloadCanvas,
  onPreviewSettled,
}: Args) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [renderParsleyPreview] = useRenderParsleyPreviewMutation();
  const [stagedCodeId, setStagedCodeId] = useState<string | null>(null);
  const stagedCodeIdRef = useRef<string | null>(null);
  const [preview, setPreviewState] = useState<StudioAiPreview | null>(null);
  // Synchronous mirror of preview, for getAiState: undo history reads it in
  // the same tick as the change that set it.
  const previewRef = useRef<StudioAiPreview | null>(null);
  const onPreviewSettledRef = useRef(onPreviewSettled);
  onPreviewSettledRef.current = onPreviewSettled;
  const setPreview = useCallback((next: StudioAiPreview | null) => {
    previewRef.current = next;
    setPreviewState(next);
    if (next && next.status !== "loading") onPreviewSettledRef.current?.();
  }, []);
  const previewRequestRef = useRef(0);
  // The last staged AI change dropped without a save, and whose page it was,
  // so that page's transcript can say so.
  const [lastDiscard, setLastDiscard] = useState<{
    id: number;
    pageItemZUID: string;
  } | null>(null);
  const savedRef = useRef(false);
  const stagedPageRef = useRef("");
  // The paint the canvas has yet to acknowledge.
  // Kept past its timeout, so a late reply still counts.
  const pendingPaintRef = useRef<{
    request: number;
    timer: ReturnType<typeof setTimeout> | null;
  } | null>(null);
  const paintSeqRef = useRef(0);
  // The last render PVL returned, painted again if the canvas reloads while
  // the change is staged.
  const lastPaintRef = useRef<{ codeId: string; html: string } | null>(null);

  // Freestyle layouts live in a per-item `/z/pvl/` view the model does not
  // render through, so they are never the target.
  const pageView = useMemo(
    () =>
      webViews.find(
        (view) =>
          !!pageModelZUID &&
          view?.contentModelZUID === pageModelZUID &&
          !view.fileName?.startsWith("/z/pvl/")
      ) || null,
    [pageModelZUID, webViews]
  );

  const readSource = useCallback(() => {
    if (!pageView) return "";
    return readStagedLayoutSource(pageView.ZUID) ?? pageView.code ?? "";
  }, [pageView, readStagedLayoutSource]);

  const describePreviewError = useCallback(
    (error: unknown, source: string) => {
      const status = (error as FetchBaseQueryError)?.status;
      const seconds = PVL_TIMEOUT_MS / 1000;
      if (status === "TIMEOUT_ERROR") {
        return source.includes("autolayout(")
          ? t("content.studioAiPreviewTimeoutAutolayout", {
              seconds,
              call: "{{this.autolayout()}}",
            })
          : t("content.studioAiPreviewTimeout", { seconds });
      }
      // Only a 200 carries CORS headers, so every error PVL answers (a Parsley
      // 400, the edge's content-filter 403, the load balancer's 413) reaches
      // the browser as a failed fetch with no status to read.
      const failed = t("content.studioAiPreviewRejected");
      return loadsExternalStylesheet(source)
        ? `${failed} ${t("content.studioAiPreviewStylesheet")}`
        : failed;
    },
    [t]
  );

  const clearPendingPaint = useCallback(() => {
    const timer = pendingPaintRef.current?.timer;
    if (timer) clearTimeout(timer);
    pendingPaintRef.current = null;
  }, []);

  const paint = useCallback(
    (codeId: string, html: string) => {
      clearPendingPaint();
      lastPaintRef.current = { codeId, html };
      setPreview({ status: "loading" });
      const pending: NonNullable<typeof pendingPaintRef.current> = {
        request: ++paintSeqRef.current,
        timer: null,
      };
      pending.timer = setTimeout(() => {
        pending.timer = null;
        if (pendingPaintRef.current !== pending) return;
        setPreview({
          status: "error",
          message: t("content.studioAiPaintNoReply"),
        });
      }, PAINT_TIMEOUT_MS);
      pendingPaintRef.current = pending;
      postCommandToBridge({
        action: "replaceCodeRegion",
        codeId,
        html,
        requestId: pending.request,
      });
    },
    [clearPendingPaint, postCommandToBridge, setPreview, t]
  );

  const renderPreview = useCallback(
    async (view: string, codeId: string) => {
      const request = ++previewRequestRef.current;
      clearPendingPaint();
      lastPaintRef.current = null;
      // Multipart encoding sends every line ending as CRLF.
      const bytes = new TextEncoder().encode(
        view.replace(/\r\n|\r|\n/g, "\r\n")
      ).length;
      if (bytes > PVL_MAX_PARSLEY_BYTES) {
        setPreview({
          status: "error",
          message: t("content.studioAiPreviewTooLarge"),
        });
        return;
      }
      // Without the instance's hash the request would go to the bare preview
      // domain, carrying the user's token.
      if (!randomHashID) {
        setPreview({
          status: "error",
          message: t("content.studioAiPreviewNoInstance"),
        });
        return;
      }
      setPreview({ status: "loading" });
      let html: string;
      try {
        html = await renderParsleyPreview({
          // Always https: the dev config's http:// preview host redirects, and
          // a preflighted request cannot follow a redirect.
          origin: `https://${randomHashID}${CONFIG.URL_PREVIEW}`,
          parsley: view,
          itemZUID: pageItemZUID,
          timeout: PVL_TIMEOUT_MS,
        }).unwrap();
      } catch (error) {
        if (request !== previewRequestRef.current) return;
        setPreview({
          status: "error",
          message: describePreviewError(error, view),
        });
        return;
      }
      if (request !== previewRequestRef.current) return;
      paint(codeId, html);
    },
    [
      clearPendingPaint,
      describePreviewError,
      pageItemZUID,
      paint,
      randomHashID,
      renderParsleyPreview,
      setPreview,
      t,
    ]
  );

  const handleCodeRegionReplaced = useCallback(
    (msg: CodeRegionReplaced) => {
      const pending = pendingPaintRef.current;
      if (!pending || msg.requestId !== pending.request) return;
      clearPendingPaint();
      setPreview(
        msg.ok
          ? { status: "painted" }
          : { status: "error", message: t("content.studioAiPaintNotFound") }
      );
    },
    [clearPendingPaint, setPreview, t]
  );

  // The other page a canvas last reported, after reloading it once. A page
  // that answers under the same other path again is redirected there and is
  // painted; a different path is reloaded again.
  const reloadedFromRef = useRef<string | null>(null);

  // The canvas reloaded under a staged change (a save of other edits, a
  // discard, a permission clamp): lock it again and repaint the last render.
  // Back moves only the iframe, so a canvas now on another page is sent back
  // instead: this page's render does not belong in it. An older bridge
  // reports no path.
  const handleBridgeReady = useCallback(
    (path?: string) => {
      if (!stagedCodeIdRef.current) return;
      const reported = path === undefined ? null : comparablePath(path);
      if (
        reported !== null &&
        reported !== comparablePath(loadedPath) &&
        reported !== reloadedFromRef.current
      ) {
        reloadedFromRef.current = reported;
        reloadCanvas();
        return;
      }
      reloadedFromRef.current = null;
      postCommandToBridge({ action: "setPreviewLock", locked: true });
      const last = lastPaintRef.current;
      if (last) paint(last.codeId, last.html);
    },
    [loadedPath, paint, postCommandToBridge, reloadCanvas]
  );

  useEffect(() => () => clearPendingPaint(), [clearPendingPaint]);

  const accepts = (next: string) =>
    typeof next === "string" && !isNotPageCode(next, readSource());
  const stage = (next: string) => {
    if (!pageView || !accepts(next)) return;
    if (!next.trim()) {
      dispatch(
        notify({ kind: "warn", message: t("content.studioAiEmptyFile") })
      );
      return;
    }
    onBeforeStage();
    postCommandToBridge({ action: "setPreviewLock", locked: true });
    stageLayoutSourceUpdate(pageView.ZUID, next, { replacesSource: true });
    stagedCodeIdRef.current = pageView.ZUID;
    stagedPageRef.current = pageItemZUID;
    setStagedCodeId(pageView.ZUID);
    void renderPreview(next, pageView.ZUID);
  };
  const stageRef = useRef(stage);
  stageRef.current = stage;
  const acceptsRef = useRef(accepts);
  acceptsRef.current = accepts;

  const handle = useMemo(
    () => ({
      accepts: (next: string) => acceptsRef.current(next),
      setValue: (next: string) => stageRef.current(next),
    }),
    []
  );
  const context = useCallback(
    () => ({
      fileName: pageView?.fileName,
      code: readSource(),
      fields: pageFields,
    }),
    [pageFields, pageView?.fileName, readSource]
  );
  // Registered with a stable context: re-registering deletes the key in a
  // passive cleanup, and a reply handled in that same commit found no ref.
  const contextRef = useRef(context);
  contextRef.current = context;
  const stableContext = useCallback(() => contextRef.current(), []);
  const isRegistered = active && !!pageView;
  useRegisterRef("code-editor", isRegistered ? handle : null, stableContext, {
    skip: !isRegistered,
  });

  // A save or a discard takes the view out of the pending set; either way the
  // live canvas is current again.
  useEffect(() => {
    if (!stagedCodeId || pendingLayoutCodeIds.includes(stagedCodeId)) return;
    previewRequestRef.current++;
    clearPendingPaint();
    lastPaintRef.current = null;
    reloadedFromRef.current = null;
    // No unlock: every way out reloads the frame, and the reloaded bridge
    // starts unlocked. Unlocking first would let the painted page post.
    stagedCodeIdRef.current = null;
    setStagedCodeId(null);
    setPreview(null);
    if (!savedRef.current) {
      endStudioChatSession(stagedPageRef.current);
      setLastDiscard({
        id: ++discardSeq,
        pageItemZUID: stagedPageRef.current,
      });
    }
    savedRef.current = false;
  }, [pendingLayoutCodeIds, setPreview, stagedCodeId]);

  // A reload or a closed tab would drop the staged change without a word; the
  // in-app prompt only sees navigation the router handles.
  useEffect(() => {
    if (!stagedCodeId) return;
    const handleBeforeUnload = (evt: BeforeUnloadEvent) => {
      evt.preventDefault();
      // Chrome only prompts when returnValue is set.
      evt.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [stagedCodeId]);

  const handleLayoutRegionSaved = useCallback((codeId: string) => {
    if (codeId !== stagedCodeIdRef.current) return;
    savedRef.current = true;
  }, []);

  const getAiState = useCallback(
    (): StudioAiState => ({
      stagedCodeId: stagedCodeIdRef.current,
      preview: previewRef.current,
      lastPaint: lastPaintRef.current,
    }),
    []
  );

  // Puts an AI change back exactly as getAiState captured it. Silent on
  // purpose: undo history restores the region's staged source alongside, in
  // the same batch, so the leave-pending effect above never sees the staged
  // view missing from the pending set and does not announce a discard. The
  // canvas DOM is restored by the bridge's own history snapshot, so nothing
  // is rendered or painted here.
  const restoreAiState = useCallback(
    (state: StudioAiState) => {
      // The server session still holds the AI turn this undo or redo crosses.
      endStudioChatSession(pageItemZUID);
      previewRequestRef.current++;
      clearPendingPaint();
      reloadedFromRef.current = null;
      lastPaintRef.current = state.lastPaint;
      stagedCodeIdRef.current = state.stagedCodeId;
      if (state.stagedCodeId) stagedPageRef.current = pageItemZUID;
      setStagedCodeId(state.stagedCodeId);
      previewRef.current = state.preview;
      setPreviewState(state.preview);
      postCommandToBridge({
        action: "setPreviewLock",
        locked: !!state.stagedCodeId,
      });
    },
    [clearPendingPaint, pageItemZUID, postCommandToBridge]
  );

  // Says in the page's transcript that its staged change was dropped. A
  // discard by reload does this as the staged view leaves the pending set;
  // undoing back to the saved state restores silently, so Cancel's no-reload
  // path calls this itself.
  const announceDiscard = useCallback(() => {
    setLastDiscard({
      id: ++discardSeq,
      pageItemZUID: stagedPageRef.current || pageItemZUID,
    });
  }, [pageItemZUID]);

  // Whether a PVL render or its paint is still on its way to the canvas.
  const isRenderPending = useCallback(
    () => previewRef.current?.status === "loading",
    []
  );

  return {
    pageView,
    isPreviewing: !!stagedCodeId,
    preview,
    lastDiscard,
    handleLayoutRegionSaved,
    handleCodeRegionReplaced,
    handleBridgeReady,
    getAiState,
    restoreAiState,
    announceDiscard,
    isRenderPending,
  };
};
